import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import './PageTitle.css';
import './PageTitle.scss';
import { formatBrowserTitle } from './pageTitleText';

const MOBILE_TITLE_SLOT_ID   = 'nav-mobile-page-title';
const MOBILE_ACTIONS_SLOT_ID = 'nav-mobile-page-actions';

/** Nested PageTitles (e.g. list inside a tab) must not fight for the chrome slots. */
const PageTitleHoistContext = createContext(false);

/**
 * Only one PageTitle may own the mobile chrome slots at a time. Sibling titles
 * (print-only headers, section headings) otherwise stack in the same DOM node.
 */
let mobile_chrome_owner_id = null;

/** First mounted page-level PageTitle owns document.title (embedded lists must not steal it). */
let browser_title_owner_id = null;

const PageTitle = props => {
    const { icon, imageUrl, imageClassName, prefix, title, subtitle, tag, actions, children } = props;
    const { isMobile, showNav, showChangelog, showHealthChecks, showSynchronisationPanel } =
        useSelector(state => state.ui);
    const nested_under_page_title = useContext(PageTitleHoistContext);
    const owner_id = useId();

    const Tag           = tag || 'h3';
    const iconClassname = icon ? "fa fa-" + icon : null;
    const image_class   = imageClassName || 'PageTitle__poster';
    const has_poster    = Boolean(imageUrl) && image_class === 'PageTitle__poster';
    const has_prefix    = prefix != null && prefix !== '';
    const has_actions   = Boolean(actions && (Array.isArray(actions) ? actions.length > 0 : true));
    const has_children  = Boolean(children);
    // Explicit hoist={false} opts out (print headers, section titles). Default
    // remains auto-hoist on mobile when this is the outermost PageTitle.
    const wants_hoist = props.hoist !== false && Boolean(
        !nested_under_page_title
        && isMobile
        && !showNav
        && !showChangelog
        && !showHealthChecks
        && !showSynchronisationPanel
    );
    const [owns_slot, set_owns_slot] = useState(false);
    const hoist = wants_hoist && owns_slot;
    const browserTitleText = formatBrowserTitle(title, prefix);
    const manages_browser_title = props.hoist !== false && !nested_under_page_title;

    const [title_target, set_title_target]     = useState(null);
    const [actions_target, set_actions_target] = useState(null);

    useEffect(() => {
        if (!wants_hoist) {
            if (mobile_chrome_owner_id === owner_id)
                mobile_chrome_owner_id = null;
            set_owns_slot(false);
            set_title_target(null);
            set_actions_target(null);
            return undefined;
        }

        if (mobile_chrome_owner_id == null || mobile_chrome_owner_id === owner_id) {
            mobile_chrome_owner_id = owner_id;
            set_owns_slot(true);
        } else {
            set_owns_slot(false);
            set_title_target(null);
            set_actions_target(null);
            return undefined;
        }

        const resolve = () => {
            set_title_target(document.getElementById(MOBILE_TITLE_SLOT_ID));
            set_actions_target(document.getElementById(MOBILE_ACTIONS_SLOT_ID));
        };
        resolve();
        const timer = window.setTimeout(resolve, 0);
        return () => {
            window.clearTimeout(timer);
            if (mobile_chrome_owner_id === owner_id)
                mobile_chrome_owner_id = null;
        };
    }, [wants_hoist, owner_id]);

    // Keep the tab / history entry title in sync with the visible page heading so
    // SPA navigations remain distinguishable in the browser back/forward list.
    useEffect(() => {
        if (!manages_browser_title)
            return undefined;
        if (browser_title_owner_id != null && browser_title_owner_id !== owner_id)
            return undefined;

        browser_title_owner_id = owner_id;
        const previousTitle = document.title;
        document.title = browserTitleText;
        return () => {
            if (browser_title_owner_id === owner_id)
                browser_title_owner_id = null;
            if (document.title === browserTitleText)
                document.title = previousTitle;
        };
    }, [browserTitleText, manages_browser_title, owner_id]);

    const heading_icon = imageUrl ? (
        <img src={imageUrl} alt="" className={image_class} />
    ) : iconClassname ? (
        <i className={iconClassname} aria-hidden="true" />
    ) : null;

    const heading_label = has_prefix ? (
        <>
            <span className="PageTitle__prefix">{prefix}</span>
            <span className="PageTitle__sep"> | </span>
            <span className="PageTitle__name">{title}</span>
        </>
    ) : title;

    const compact_heading = (
        <div className="Nav__mobilePageTitleInner">
            {icon && !imageUrl ? <i className={`fa fa-${icon}`} aria-hidden="true" /> : null}
            <span className="Nav__mobilePageTitleText">{heading_label}</span>
        </div>
    );

    return (
        <PageTitleHoistContext.Provider value={true}>
            <Tag
                className={[
                    'PageTitle',
                    hoist ? 'PageTitle--hoisted' : '',
                    has_poster ? 'PageTitle--hasPoster' : '',
                    has_actions ? 'PageTitle--hasActions' : '',
                    has_children ? 'PageTitle--hasChildren' : '',
                ].filter(Boolean).join(' ')}
            >
                {hoist && title_target ? createPortal(compact_heading, title_target) : null}
                {hoist && has_actions && actions_target
                    ? createPortal(actions, actions_target)
                    : null}
                {!hoist && (
                    <div className="PageTitle__row">
                        <div className="PageTitle__heading">
                            {heading_icon}
                            <div className="PageTitle__titles">
                                <span className="PageTitle__text">{heading_label}</span>
                                {subtitle ? (
                                    <span className="PageTitle__subtitle">{subtitle}</span>
                                ) : null}
                            </div>
                        </div>
                        {has_actions && (
                            <div className="PageTitle-actions">
                                {actions}
                            </div>
                        )}
                    </div>
                )}
                { children }
            </Tag>
        </PageTitleHoistContext.Provider>
    );
};

export default PageTitle;
